import { useState } from 'react';
import type { ComponentProps } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { EChartsType } from 'echarts/core';
import { Box } from '@mui/material';

import type {
  TimeSeriesChartAccessibility,
  TimeSeriesChartProps,
} from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { echarts } from '../echartsSetup';
import { CHART_INIT_OPTIONS } from './config/chartInitOptions';
import { useChartOptionSync } from './hooks/useChartOptionSync';
import { useNearestSeriesTooltip } from './hooks/useNearestSeriesTooltip';
import { useTimeSeriesChartOption } from './hooks/useTimeSeriesChartOption';
import { createTooltipContent } from './tooltip/content/createTooltipContent';

type EChartsTimeSeriesChartRendererProps = TimeSeriesChartProps & {
  theme: TimeSeriesChartTheme;
};

type AccessibilityProps = Pick<
  ComponentProps<typeof ReactEChartsCore>,
  'aria-description' | 'aria-hidden' | 'aria-label' | 'role'
>;

const CHART_STYLE = { height: '100%', width: '100%' } as const;

function buildAccessibilityProps(accessibility: TimeSeriesChartAccessibility): AccessibilityProps {
  if (accessibility.isDecorative) {
    return { 'aria-hidden': true };
  }

  return {
    role: 'img',
    'aria-label': accessibility.label,
    'aria-description': accessibility.description,
  };
}

/**
 * ECharts implementation of TimeSeriesChart. The pieces, in data-flow order:
 * - useTimeSeriesChartOption: props + the renderer-owned viewport → ECharts option;
 * - useChartOptionSync: applies every later option imperatively, keeping the user's zoom;
 * - useNearestSeriesTooltip: the grouped tooltip, driven by pointer events.
 */
export function EChartsTimeSeriesChartRenderer({
  series,
  rangeTrack,
  timeDomain,
  highlight,
  accessibility,
  navigation,
  isTooltipEnabled = false,
  sectionLabels,
  theme,
}: EChartsTimeSeriesChartRendererProps) {
  const [chart, setChart] = useState<EChartsType>();

  // One holder for the chart's lifetime: the interaction writes it, the formatter reads it.
  // State rather than useMemo, because React may drop a memo cache and the formatter would go stale.
  const [tooltipContent] = useState(createTooltipContent);

  const { option, containerRef } = useTimeSeriesChartOption({
    chart,
    series,
    rangeTrack,
    timeDomain,
    highlight,
    navigation,
    isTooltipEnabled,
    sectionLabels,
    theme,
    renderTooltipContent: tooltipContent.render,
  });

  const initialOption = useChartOptionSync({ chart, option, timeDomain, navigation });

  useNearestSeriesTooltip({ chart, series, isEnabled: isTooltipEnabled, setTooltipCards: tooltipContent.setCards });

  return (
    // The box only measures the plot width (see usePlotWidth); everything visible is on the canvas.
    <Box ref={containerRef} sx={CHART_STYLE}>
      <ReactEChartsCore
        {...buildAccessibilityProps(accessibility)}
        echarts={echarts}
        notMerge={false}
        option={initialOption}
        onChartReady={setChart}
        opts={CHART_INIT_OPTIONS}
        style={CHART_STYLE}
      />
    </Box>
  );
}
